import { startupProfileOptIn } from './startup-profile-guard';
import { Profiler, type ReactElement, type ProfilerOnRenderCallback } from 'react';

declare const __STARTUP_PROFILE__: boolean;
export const PROFILE_NAMES = ['application', 'table.children', 'rules.children', 'budget.children', 'thumb.children', 'spark.children'] as const;
export type ProfileName = typeof PROFILE_NAMES[number];
type Phase = 'mount' | 'update' | 'nested-update';
export type ProfileEntry = { name: ProfileName; phase: Phase; actualRenderMs: number; baseRenderMs: number; renderStartMs: number; commitStartMs: number; callbackObservedMs: number; commitStartToCallbackMs: number };
const round = (n: number) => Math.round(n * 10) / 10;
export class StartupProfileBuffer {
  private readonly rows: ProfileEntry[] = [];
  add(name: string, phase: string, actual: number, base: number, start: number, commit: number, observed: number): void {
    if (this.rows.length >= 512 || !(PROFILE_NAMES as readonly string[]).includes(name) || !['mount', 'update', 'nested-update'].includes(phase)) return;
    if (![actual, base, start, commit, observed].every(n => Number.isFinite(n) && n >= 0) || commit < start || observed < commit) return;
    this.rows.push({ name: name as ProfileName, phase: phase as Phase, actualRenderMs: round(actual), baseRenderMs: round(base), renderStartMs: round(start), commitStartMs: round(commit), callbackObservedMs: round(observed), commitStartToCallbackMs: round(observed - commit) });
  }
  snapshot(): ProfileEntry[] { return this.rows.map(r => ({ ...r })); }
}
// This decision is fixed for the page. Ending recording or SPA navigation must
// never add/remove profiler wrappers and remount an open form.
const profileTree = __STARTUP_PROFILE__ && typeof window !== 'undefined' && startupProfileOptIn(window.location.search);
const buffer = new StartupProfileBuffer();
let recording = false;
let scheduleReport: (() => void) | undefined;
const onRender: ProfilerOnRenderCallback = (name, phase, actual, base, start, commit) => {
  if (!recording) return;
  buffer.add(name, phase, actual, base, start, commit, performance.now());
  scheduleReport?.();
};
/** The ordinary build returns the exact same element without profiling work. */
export function profileStartup(name: ProfileName, child: ReactElement): ReactElement {
  return profileTree ? <Profiler id={name} onRender={onRender}>{child}</Profiler> : child;
}
export function startStartupProfile(): void {
  if (!profileTree || recording) return;
  recording = true;
  const panel = document.createElement('details'); panel.id = 'startup-profile';
  panel.style.cssText = 'position:fixed;left:8px;bottom:8px;z-index:10001;max-width:420px;max-height:160px;overflow:auto;background:var(--bg-surface,#fff);color:var(--ink,#111);border:1px solid #aaa;padding:5px;font:11px monospace';
  const title = document.createElement('summary'); title.textContent = 'Профиль рендера';
  const output = document.createElement('pre'); output.id = 'startup-profile-report';
  panel.append(title, output); document.body.append(panel);
  let queued = false;
  const flush = () => {
    queued = false;
    output.textContent = JSON.stringify({ schema: 1, profilingBuild: true, recording, entries: buffer.snapshot(), note: 'Local static labels and timing only. The application profiler includes its React render tree; named children profilers exclude the containing function body. Subtrees overlap: do not add all durations. commitStartToCallbackMs includes DOM mutation, preceding layout effects and profiler callbacks; it is not an exact DOM-only duration. Profiling adds overhead, so use the ordinary build to confirm an optimization. No props, identifiers, sessions or telemetry are recorded.' });
  };
  scheduleReport = () => { if (!queued) { queued = true; window.setTimeout(flush, 0); } };
  const stop = () => { recording = false; scheduleReport = undefined; flush(); };
  window.addEventListener('pagehide', stop, { once: true }); window.setTimeout(stop, 120_000); flush();
}
